import { P, match } from 'ts-pattern';

import { isSoccerLeague } from '../interfaces/propsService/api/leagues';

type MatchupTeam = {
  alias?: string;
  id?: string;
};

export type PlayerTeamFirstMatchup = {
  leftAlias: string;
  rightAlias: string;
  separator: '@' | 'vs.';
  highlightedAlias: string | null;
};

export function shouldRenderHomeFirst(league: string | undefined): boolean {
  return isSoccerLeague(league);
}

export function getPlayerTeamFirstMatchup(params: {
  awayTeam?: MatchupTeam;
  homeTeam?: MatchupTeam;
  playerTeamId?: string;
  league?: string;
}): PlayerTeamFirstMatchup | null {
  const awayTeamAlias = params?.awayTeam?.alias;
  const homeTeamAlias = params?.homeTeam?.alias;

  if (!awayTeamAlias || !homeTeamAlias) return null;
  const awayTeamId = params?.awayTeam?.id;
  const homeTeamId = params?.homeTeam?.id;
  const { playerTeamId } = params;

  if (shouldRenderHomeFirst(params.league)) {
    const highlightedAlias = match(playerTeamId)
      .with(
        P.when((id) => !!(id && homeTeamId && id === homeTeamId)),
        () => homeTeamAlias
      )
      .with(
        P.when((id) => !!(id && awayTeamId && id === awayTeamId)),
        () => awayTeamAlias
      )
      .otherwise(() => null);

    return {
      leftAlias: homeTeamAlias,
      rightAlias: awayTeamAlias,
      separator: 'vs.' as const,
      highlightedAlias,
    };
  }

  return match(playerTeamId)
    .with(
      P.when((id) => !!(id && homeTeamId && id === homeTeamId)),
      () => ({
        leftAlias: homeTeamAlias,
        rightAlias: awayTeamAlias,
        separator: 'vs.' as const,
        highlightedAlias: homeTeamAlias,
      })
    )
    .with(
      P.when((id) => !!(id && awayTeamId && id === awayTeamId)),
      () => ({
        leftAlias: awayTeamAlias,
        rightAlias: homeTeamAlias,
        separator: '@' as const,
        highlightedAlias: awayTeamAlias,
      })
    )
    .otherwise(() => ({
      leftAlias: awayTeamAlias,
      rightAlias: homeTeamAlias,
      separator: '@' as const,
      highlightedAlias: null,
    }));
}

export function formatPlayerTeamFirstGameDescription(params: {
  awayTeam?: { alias?: string; id?: string };
  homeTeam?: { alias?: string; id?: string };
  playerTeamId?: string;
  league?: string;
}): string {
  const matchup = getPlayerTeamFirstMatchup(params);
  if (!matchup) return '—';

  return `${matchup.leftAlias} ${matchup.separator} ${matchup.rightAlias}`;
}
